import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { InternalGrid, Box } from '@paygreen/paygreen-ui';
import { useAPIError, useMode } from '../../../context';
import { IsBigDesktop, IsDesktop, formatDateApi } from '../../../utils/tools';
import { endOfMonth, startOfMonth } from '../../../utils/constants';
import { ErrorBoundary, ToggleMode, H1Title } from '../../../components';
import DonationsTable from '../../donations/DonationsTable';
import FilterTable from '../../donations/FilterTable';
import { getAllDonations } from '../../donations/service';
import { MODES } from '../../../utils/constants';

const AdminUserDonations = ({ t, accountId, idAssociation }) => {
    const { addError } = useAPIError();
    const { mode } = useMode();
    const fetchIdRef = useRef(0);

    const isCharityDonations = !!useRouteMatch(
        '/admin/charities/:accountId/charity-donations',
    );

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [activePageReset, setActivePageReset] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');

    const [startDate, setStartDate] = useState(startOfMonth);
    const [endDate, setEndDate] = useState(endOfMonth);

    // to reset pageIndex and filters when mode changes
    const handleModeChange = () => {
        setActivePageReset(true);
        setStatusFilter('');
    };

    const fetchData = useCallback(
        ({
            pageSize,
            pageIndex,
            statusFilter,
            startDate,
            endDate,
            accountId,
            idAssociation,
        }) => {
            const fetchId = ++fetchIdRef.current;

            if (fetchId === fetchIdRef.current) {
                isMounted.current && setIsLoading(true);

                const params = {
                    limit: pageSize,
                    page: pageIndex + 1,
                    status: statusFilter,
                    begin: startDate && formatDateApi(startDate),
                    end: endDate && formatDateApi(endDate),
                    idAccount: isCharityDonations ? null : accountId,
                    idAssociation: isCharityDonations ? idAssociation : null,
                };

                if (
                    (isCharityDonations && idAssociation) || // to prevent double requests during mounting, for each case we wait for params to be defined
                    (!isCharityDonations && accountId)
                ) {
                    getAllDonations(
                        params,
                        isCharityDonations ? MODES.PROD : mode,
                    )
                        .then((res) => {
                            const {
                                page_count,
                                total_items,
                                _embedded: { donation },
                            } = res.data;

                            if (isMounted.current) {
                                setData(donation);
                                setPageCount(page_count);
                                setTotalItems(total_items);
                            }
                        })
                        .catch((error) => {
                            const { status, detail } = error?.response?.data;

                            if (status === 404) {
                                isMounted.current && setData([]);
                            } else {
                                addError(detail, status);
                            }
                        })
                        .finally(() => {
                            isMounted.current && setIsLoading(false);
                        });
                }
            }
        },
        [addError, mode, isCharityDonations],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <H1Title
                title={t('shopDonations.title')}
                textSize="md"
                htmlTag="h2"
                colorWab="grey50"
            >
                {!isCharityDonations && (
                    <ToggleMode externalChange={handleModeChange} />
                )}
            </H1Title>

            <InternalGrid
                displayType="grid"
                gridGap="md"
                gridTemplateColumns="1fr"
                blockPadding="none"
                justifyItems="stretch"
                hasStaticWidth={IsBigDesktop() ? false : true}
            >
                <Box
                    marginBottom={IsDesktop() ? 'lg' : 'md'}
                    style={{ width: 'fit-content' }}
                >
                    <FilterTable
                        setEndDate={setEndDate}
                        setStartDate={setStartDate}
                        startDate={startDate}
                        endDate={endDate}
                        hasMinimumDate={false}
                    />
                </Box>

                <ErrorBoundary>
                    <DonationsTable
                        fetchData={fetchData}
                        data={data}
                        loading={isLoading}
                        pageCount={pageCount}
                        startDate={startDate}
                        endDate={endDate}
                        totalItems={totalItems}
                        setActivePageReset={setActivePageReset}
                        activePageReset={activePageReset}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        accountId={accountId}
                        idAssociation={idAssociation}
                    />
                </ErrorBoundary>
            </InternalGrid>
        </>
    );
};

AdminUserDonations.propTypes = {
    accountId: PropTypes.string,
    idAssociation: PropTypes.string,
};

AdminUserDonations.defaultProps = {
    accountId: '',
    idAssociation: '',
};

export default withTranslation('common')(AdminUserDonations);
